import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Flex, Text, Tooltip } from '@cko/primitives';
import { IconActionChevronRight } from '@cko/icons';

interface AccountSelectorListItemProps {
  title: string;
  nestedCount?: number;
  isSelected?: boolean;
  isParent?: boolean;
  isInactive?: boolean;
  isDisabled?: boolean;
  disabledMessage?: string;
  onClick?: () => void;
}

const hoverStyle = `&:hover {
  background: var(--control-bg-decorative-hover);
  color: var(--control-text-primary);
}`;

const Container = styled.button<{
  $isSelected?: boolean;
  $isParent?: boolean;
  $isInactive?: boolean;
  $isDisabled?: boolean;
}>`
  display: flex;
  border: none;
  text-align: left;
  cursor: pointer;
  width: 280px;
  padding: var(--layout-soybean) var(--layout-stamp);
  align-items: center;
  justify-content: space-between;
  gap: var(--layout-soybean);
  background: var(--control-bg-primary);
  border-radius: var(--border-r-stamp);
  color: var(--control-text-primary);
  ${({ $isSelected, $isParent, $isInactive, $isDisabled }) => {
    // order of priority - isDisabled, $isSelectedParent, isSelected and finally isInactive
    if ($isDisabled) {
      return css`
        color: var(--control-text-disabled);
        cursor: not-allowed;
      `;
    }

    if ($isSelected) {
      if ($isParent) {
        return css`
          background: var(--control-bg-light-selected);
          color: var(--control-text-decorative-selected);
          ${hoverStyle}
        `;
      }

      return css`
        background: var(--control-bg-selected);
        color: var(--control-text-oninvert-selected);
      `;
    }

    if ($isInactive) {
      return css`
        color: var(--control-text-secondary);
        ${hoverStyle}
      `;
    }
    return css`
      &:focus,
      &:focus-visible {
        outline-offset: calc(0px - var(--border-b-soybean));
        background: var(--control-bg-decorative-focus);
        color: var(--control-text-focus);
      }
      ${hoverStyle}
    `;
  }};
`;

const Label = styled(Text)`
  align-self: stretch;
  font: var(--body-xs-medium);
  width: 216px;
  overflow-wrap: anywhere;
  word-wrap: break-word;
  color: inherit;
`;

const NestedCountContainer = styled(Flex)`
  font: var(--helper-sm);
  align-items: center;
  gap: var(--layout-soybean);
  color: inherit;
`;

const NestedCountChevron = styled(IconActionChevronRight)`
  fill: currentColor !important;
`;

const IsInactive = styled(Text)`
  color: inherit;
  font: var(--helper-sm);
`;

export const AccountSelectorListItem = ({
  title,
  nestedCount,
  isSelected,
  isParent,
  isInactive,
  isDisabled,
  disabledMessage,
  onClick,
}: AccountSelectorListItemProps) => {
  const showNestedCount = !!(nestedCount && nestedCount > 0);

  const handleClick = useCallback(() => {
    if (!isDisabled && onClick) {
      onClick();
    }
  }, [onClick, isDisabled]);

  const item = useMemo(
    () => (
      <Container
        className={
          isSelected ? 'account-selector-list-item-selected' : 'account-selector-list-item'
        }
        onClick={handleClick}
        $isParent={isParent}
        $isSelected={isSelected}
        $isInactive={isInactive}
        $isDisabled={isDisabled}
      >
        <Label>{title}</Label>

        {isInactive && <IsInactive>Inactive</IsInactive>}
        {showNestedCount && (
          <NestedCountContainer>
            {nestedCount}
            <NestedCountChevron width="16px" height="16px" />
          </NestedCountContainer>
        )}
      </Container>
    ),
    [
      handleClick,
      isParent,
      isSelected,
      isInactive,
      isDisabled,
      title,
      showNestedCount,
      nestedCount,
    ],
  );

  if (isDisabled) {
    return <Tooltip text={disabledMessage}>{item}</Tooltip>;
  }
  return item;
};
